import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Loader2, Trash2, Pencil, Plug, CheckCircle2, AlertCircle,
  Link2, Unlink, KeyRound, PackagePlus, Sparkles, Info, BookOpen,
  Ban, XCircle,
} from "lucide-react";

const STATUS_META = {
  connected: { label: "Conectado", color: "#059669", icon: CheckCircle2 },
  disconnected: { label: "Não conectado", color: "#9CA3AF", icon: Plug },
  missing_credentials: { label: "Credenciais ausentes", color: "#DC2626", icon: AlertCircle },
  token_expired: { label: "Token expirado", color: "#D97706", icon: AlertCircle },
  error: { label: "Erro", color: "#DC2626", icon: AlertCircle },
  cancelled: { label: "Cobrança cancelada", color: "#DC2626", icon: Ban },
};

const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

function SlotUsageCard({ sub, onBuySlot, buying }) {
  if (!sub) return null;
  const used = sub.used_blings ?? 0;
  const limit = sub.bling_limit ?? 1;
  const remaining = Math.max(0, limit - used);
  const trial = sub.trial_active;
  const canBuy = !trial && sub.can_use;
  const barPct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const barColor = remaining === 0 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="border border-slate-200 bg-white p-5" data-testid="integrations-slot-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            {trial ? "Trial · 7 dias grátis" : "Sua assinatura"}
          </div>
          <div className="text-2xl font-black tracking-tight text-slate-900 mt-0.5">
            {used} <span className="text-slate-400 font-mono font-normal">/</span>{" "}
            <span className="text-slate-500 font-mono">{limit}</span>
            <span className="ml-2 text-sm font-bold text-slate-500 normal-case tracking-normal">
              {limit === 1 ? "Bling conectado" : "Blings conectados"}
            </span>
          </div>
          {trial ? (
            <p className="text-xs text-slate-600 mt-2 max-w-lg">
              No período de teste você pode conectar <b>até 1 Bling</b>. Depois de assinar,
              cada Bling adicional custa <b>R$ 49,90/mês</b>.
            </p>
          ) : (
            <p className="text-xs text-slate-600 mt-2 max-w-lg">
              Plano base inclui <b>1 Bling</b>. Cada Bling adicional é cobrado por{" "}
              <b>R$ 49,90/mês</b> na sua próxima fatura.
            </p>
          )}
        </div>

        {canBuy && (
          <button
            data-testid="integrations-buy-slot"
            onClick={onBuySlot}
            disabled={buying}
            className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
          >
            {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackagePlus className="w-3.5 h-3.5" />}
            Adquirir Bling adicional · {brl(49.9)}/mês
          </button>
        )}
      </div>

      {/* usage bar */}
      <div className="mt-4">
        <div className="h-2 w-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${barPct}%` }} />
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between text-[11px] text-slate-500 font-mono">
          <span>{remaining} slot{remaining !== 1 ? "s" : ""} disponível{remaining !== 1 ? "eis" : ""}</span>
          {sub.monthly_total_brl != null && !trial && (
            <span>Total mensal: <b className="text-slate-900">{brl(sub.monthly_total_brl)}</b></span>
          )}
        </div>
      </div>
    </div>
  );
}

function UnusedSlotBanner({ sub, onCancelled }) {
  const [busy, setBusy] = useState(false);
  const unused = sub.available_slots || 0;
  const perSlot = sub.extra_bling_brl || 49.9;
  const totalWaste = unused * perSlot;

  const cancel = async () => {
    if (!window.confirm(
      `Cancelar 1 slot Bling que você não está usando?\n\n` +
      `- Cobrança de ${brl(perSlot)}/mês removida do Stripe agora\n` +
      `- Próxima fatura: ${brl(sub.monthly_total_brl - perSlot)}\n` +
      `- Créditos proporcionais aplicados automaticamente\n` +
      `- Nenhum dos seus Blings conectados é afetado`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/cancel-unused-slot", {});
      if (data?.billing_synced === false && data?.stripe_error) {
        toast.warning(
          `Slot cancelado localmente, mas Stripe reportou: ${data.stripe_error}`,
          { duration: 10000 },
        );
      } else {
        toast.success(
          `Slot ocioso cancelado. Próxima fatura: ${brl(data.plan.monthly_total_brl)}/mês.`,
          { duration: 8000 },
        );
      }
      onCancelled();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao cancelar slot ocioso");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border-2 border-orange-400 bg-orange-50 px-4 py-3 flex items-start gap-3"
      data-testid="unused-slot-banner"
    >
      <AlertCircle className="w-5 h-5 text-orange-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-black text-orange-900">
          Você tem {unused} slot{unused > 1 ? "s" : ""} pago{unused > 1 ? "s" : ""} sem uso
        </div>
        <div className="text-xs text-orange-900 mt-1">
          Contratou <b>{sub.bling_limit}</b> Bling{sub.bling_limit > 1 ? "s" : ""} mas está usando só
          {" "}<b>{sub.used_blings}</b>. Está pagando <b>{brl(totalWaste)}/mês</b> por slots vazios.
        </div>
        <button
          data-testid="cancel-unused-slot-btn"
          onClick={cancel}
          disabled={busy}
          className="mt-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-orange-600 hover:bg-orange-700 text-white inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
          Cancelar 1 slot ocioso · economize {brl(perSlot)}/mês
        </button>
      </div>
    </div>
  );
}

export default function Integrations() {
  const loc = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(null);
  const [sub, setSub] = useState(null);
  const [buying, setBuying] = useState(false);
  const [cancelling, setCancelling] = useState(null); // integration being cancelled

  const load = async () => {
    setLoading(true);
    try {
      const [i, c, ct, s] = await Promise.all([
        api.get("/integrations"),
        api.get("/companies"),
        api.get("/connectors"),
        api.get("/subscription"),
      ]);
      setList((i.data || []).filter((x) => x.type === "bling"));
      setCompanies(c.data);
      setConnectors(ct.data);
      setSub(s.data);
    } catch (_e) {
      toast.error("Erro ao carregar integrações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Handle OAuth callback flash
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    if (params.get("connected")) {
      const label = params.get("label");
      toast.success(
        `✅ Bling conectado com sucesso!${label ? ` Conta: ${label}` : ""}`,
        { duration: 6000 }
      );
      navigate("/integrations", { replace: true });
    } else if (params.get("connect_error")) {
      const reason = params.get("reason") || "erro";
      const detail = params.get("detail") || "";
      const provider = params.get("connect_error");
      toast.error(
        `${provider === "bling" ? "Bling" : provider} rejeitou a conexão: ${reason}` +
        (detail ? ` — ${detail}` : "") +
        ". Verifique client_id/client_secret em Credenciais Plataforma.",
        { duration: 12000 }
      );
      navigate("/integrations", { replace: true });
    }
  }, [loc.search, navigate]);

  const usedBlings = sub?.used_blings ?? list.filter((x) => x.status === "connected").length;
  const limit = sub?.bling_limit ?? 1;
  const remaining = Math.max(0, limit - usedBlings);
  const canCreate = remaining > 0;

  const startNew = () => {
    if (!canCreate) {
      toast.error(`Você atingiu o limite de ${limit} Bling(s). Adquira um slot adicional.`);
      return;
    }
    setForm({
      type: "bling",
      name: "",
      company_id: companies[0]?.id || "",
      credentials: {},
      active: true,
    });
  };

  const save = async () => {
    try {
      if (form.id) await api.patch(`/integrations/${form.id}`, form);
      else await api.post("/integrations", { ...form, type: "bling" });
      toast.success("Integração salva");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir integração?")) return;
    try {
      await api.delete(`/integrations/${id}`);
      toast.success("Excluída");
      load();
    } catch (_e) {
      toast.error("Falha");
    }
  };

  const startOAuth = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/integrations/${id}/oauth/start`);
      // Warn about the "Bling remembers previous consent" gotcha — this
      // is the #1 source of tickets after we add new scopes.
      toast.info(
        "Se a tela de permissões do Bling não aparecer, é porque você já autorizou antes com escopos menores. " +
        "Solução: em bling.com.br → Preferências → Aplicativos → revogue 'Vyntric' → volte aqui e reconecte.",
        { duration: 14000 }
      );
      // Small delay so the toast is visible before the browser navigates away.
      setTimeout(() => { window.location.href = data.authorization_url; }, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao iniciar OAuth");
      setBusy(null);
    }
  };

  const disconnect = async (id) => {
    if (!window.confirm("Desconectar Bling? Você precisará re-autorizar para faturar novos pedidos.")) return;
    setBusy(id);
    try {
      await api.post(`/integrations/${id}/disconnect`);
      toast.success("Desconectada");
      load();
    } catch (_e) {
      toast.error("Falha");
    } finally {
      setBusy(null);
    }
  };

  const renameBling = async (integ) => {
    const current = integ.nickname || integ.name || "";
    const next = window.prompt(
      "Apelido para essa conta Bling (ex.: Loja Matriz, Filial SP):",
      current,
    );
    if (next === null) return; // user cancelled
    const trimmed = next.trim().slice(0, 80);
    if (!trimmed) {
      toast.error("Apelido não pode ficar vazio");
      return;
    }
    if (trimmed === current) return;
    try {
      await api.patch(`/integrations/${integ.id}/nickname`, { nickname: trimmed });
      toast.success(`Renomeado para "${trimmed}"`);
      load();
      // Also trigger the global BlingContext to refresh so the sidebar
      // selector picks up the new label immediately.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("vyntric:blings-changed"));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao renomear");
    }
  };

  const [scopesModal, setScopesModal] = useState(null); // { integ, data, loading }

  const openScopes = async (integ) => {
    setScopesModal({ integ, data: null, loading: true });
    try {
      const { data } = await api.get(`/integrations/${integ.id}/scopes`);
      setScopesModal({ integ, data, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar permissões");
      setScopesModal(null);
    }
  };

  const buyExtraSlot = async () => {
    if (!window.confirm(
      "Adicionar +1 slot para conectar outro Bling? " +
      "O valor de R$ 49,90/mês será somado à sua próxima fatura."
    )) return;
    setBuying(true);
    try {
      await api.post("/subscription/purchase-extra-bling", { quantity: 1 });
      toast.success("Slot adicional liberado! Ajuste será aplicado na próxima fatura.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao contratar slot adicional");
    } finally {
      setBuying(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="integrations-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Conectores</div>
          <h1 className="text-3xl font-black tracking-tighter">Integração com Bling ERP</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Conecte sua conta Bling via <span className="font-bold">OAuth</span> — sem token manual, sem
            senhas expostas. Cada Bling conectado é um <b>slot</b>.
          </p>
          <Link
            to="/instrucoes-bling"
            target="_blank"
            rel="noreferrer"
            data-testid="integrations-manual-link"
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#002EA6] hover:text-[#00207A] hover:underline"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Ver manual de integração Bling — passo a passo
          </Link>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            data-testid="integration-new"
            onClick={startNew}
            disabled={!canCreate}
            className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" /> Conectar Bling
          </button>
          <Link
            to="/instrucoes-bling"
            target="_blank"
            rel="noreferrer"
            data-testid="integrations-manual-btn"
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-slate-300 text-slate-700 hover:border-slate-900 hover:text-slate-900 flex items-center gap-1.5"
          >
            <BookOpen className="w-3 h-3" /> Como conectar?
          </Link>
        </div>
      </div>

      <SlotUsageCard sub={sub} onBuySlot={buyExtraSlot} buying={buying} />

      {sub && sub.available_slots > 0 && !sub.trial_active && limit > 1 && (
        <UnusedSlotBanner
          sub={sub}
          onCancelled={load}
        />
      )}

      {!canCreate && sub && (
        <div className="border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="integrations-limit-notice">
          <Sparkles className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>Limite de {limit} Bling atingido.</b> {sub.trial_active
              ? "Durante o trial, só é possível conectar 1 Bling. Ao assinar o plano você pode adicionar quantos precisar."
              : "Para conectar outro Bling, clique em Adquirir Bling adicional acima. Cada slot custa R$ 49,90/mês."}
          </div>
        </div>
      )}

      {form && (
        <div className="border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="integration-form">
          <div className="md:col-span-2 flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 px-3 py-2">
            <Info className="w-3.5 h-3.5 text-[#002EA6]" />
            Só integração com o <b>Bling ERP</b> está disponível no momento.
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">Empresa</span>
            <select data-testid="integration-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className="w-full border border-slate-300 px-2 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.nome_fantasia}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">Nome / Apelido</span>
            <input data-testid="integration-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-300 px-2 py-2 text-sm" placeholder="Ex: Loja Principal" />
          </label>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button onClick={() => setForm(null)} className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 hover:border-slate-900">Cancelar</button>
            <button data-testid="integration-save" onClick={save} className="px-4 py-2 text-xs font-bold uppercase bg-[#002EA6] hover:bg-[#00207A] text-white">Salvar e prosseguir</button>
          </div>
          <div className="md:col-span-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 px-3 py-2">
            Após salvar, clique em <b>Conectar via OAuth</b> no card para autenticar sua conta Bling.
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="inline w-5 h-5 animate-spin text-slate-400" /></div>
      ) : list.length === 0 ? (
        <div className="border border-slate-200 bg-white py-12 text-center text-sm text-slate-500">
          Nenhum Bling conectado ainda. Clique em <b>Conectar Bling</b> para começar.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((i) => {
            const meta = STATUS_META[i.status] || STATUS_META.disconnected;
            const Icon = meta.icon;
            const company = companies.find((c) => c.id === i.company_id);
            const conn = connectors.find((c) => c.type === i.type);
            const connected = i.status === "connected";
            return (
              <div key={i.id} className="border border-slate-200 bg-white p-4" data-testid={`integration-card-${i.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Bling ERP</span>
                      {connected && (
                        <span
                          data-testid={`integration-connected-badge-${i.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-800"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          Bling Conectado
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className="text-sm font-bold truncate flex-1">
                        {i.nickname || i.name || "(sem nome)"}
                      </div>
                      <button
                        data-testid={`integration-rename-${i.id}`}
                        onClick={() => renameBling(i)}
                        className="text-[10px] text-slate-500 hover:text-slate-900 underline decoration-dotted"
                        title="Renomear apelido desta conta Bling"
                      >
                        renomear
                      </button>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{company?.nome_fantasia}</div>
                    {i.account_label && (
                      <div className="text-[10px] font-mono text-slate-600 mt-1 truncate">→ {i.account_label}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5" style={{ color: meta.color }}>
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px] uppercase font-bold tracking-wider">{meta.label}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 pt-3 mt-2 border-t border-slate-100">
                  {conn?.auth_kind === "token" ? (
                    <Link
                      data-testid={`integration-token-info-${i.id}`}
                      to="/platform-settings"
                      className="flex-1 px-2 py-1.5 text-xs font-bold uppercase tracking-wider bg-slate-100 hover:bg-slate-200 text-slate-800 flex items-center justify-center gap-1.5 border border-slate-200"
                      title="Configure o Token da API em Credenciais de Plataforma"
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                      Autenticação por Token
                    </Link>
                  ) : !connected ? (
                    <button
                      data-testid={`integration-oauth-${i.id}`}
                      onClick={() => startOAuth(i.id)}
                      disabled={busy === i.id}
                      className="flex-1 px-2 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center justify-center gap-1.5 disabled:opacity-60"
                    >
                      {busy === i.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                      Conectar via OAuth
                    </button>
                  ) : (
                    <button
                      data-testid={`integration-disconnect-${i.id}`}
                      onClick={() => disconnect(i.id)}
                      disabled={busy === i.id}
                      className="flex-1 px-2 py-1.5 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-1.5 disabled:opacity-60"
                      title="Bling conectado — clique para desconectar"
                    >
                      {busy === i.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      )}
                      Bling Conectado
                    </button>
                  )}
                  {connected && (
                    <button
                      data-testid={`integration-scopes-${i.id}`}
                      onClick={() => openScopes(i)}
                      className="p-1.5 hover:bg-blue-100 text-blue-700"
                      title="Ver permissões (scopes) concedidas pelo Bling"
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                  )}
                  {i.status !== "cancelled" && limit > 1 && (
                    <button
                      data-testid={`integration-cancel-billing-${i.id}`}
                      onClick={() => setCancelling(i)}
                      className="p-1.5 hover:bg-orange-100 text-orange-700"
                      title="Cancelar cobrança deste Bling (mantém dados, remove das próximas faturas)"
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                  )}
                  <button data-testid={`integration-edit-${i.id}`} onClick={() => setForm(i)} className="p-1.5 hover:bg-slate-200 text-slate-600"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`integration-del-${i.id}`} onClick={() => remove(i.id)} className="p-1.5 hover:bg-red-100 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {cancelling && (
        <CancelBillingDialog
          integration={cancelling}
          sub={sub}
          onClose={() => setCancelling(null)}
          onDone={() => { setCancelling(null); load(); }}
        />
      )}

      {scopesModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setScopesModal(null)}
          data-testid="scopes-modal-backdrop"
        >
          <div
            className="bg-white max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl border-2 border-slate-900"
            onClick={(e) => e.stopPropagation()}
            data-testid="scopes-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 bg-slate-900 text-white">
              <div className="flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                <h2 className="font-black uppercase tracking-wider text-sm">
                  Permissões Bling
                </h2>
              </div>
              <button
                onClick={() => setScopesModal(null)}
                className="text-white/70 hover:text-white text-xl leading-none"
                data-testid="scopes-modal-close"
              >×</button>
            </div>
            <div className="p-5 space-y-4">
              {scopesModal.loading && (
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Consultando Bling…
                </div>
              )}
              {!scopesModal.loading && scopesModal.data && (
                <>
                  <div className="text-xs text-slate-500">
                    Integração: <b className="font-mono text-slate-800">{scopesModal.integ.name}</b>
                  </div>
                  {scopesModal.data.missing.length === 0 ? (
                    <div className="bg-emerald-50 border border-emerald-200 p-3 text-emerald-900 text-sm space-y-2">
                      <div className="font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Bling autorizou todos os escopos essenciais
                      </div>
                      <div className="text-xs">
                        Todos os {scopesModal.data.expected.length} escopos que a Vyntric solicita
                        foram concedidos ({scopesModal.data.granted.length} no total, incluindo escopos extras do Bling).
                      </div>
                      <div className="text-xs pt-2 border-t border-emerald-200">
                        <b>Se ainda estiver dando &quot;sem permissão&quot;</b>, o problema é
                        <b> permissão do usuário dentro do Bling</b>, não OAuth:
                        acesse <b>bling.com.br → Preferências → Usuários</b>, edite o
                        usuário que autorizou e certifique-se que ele tem permissão para{" "}
                        <b>Vender → Pedidos de Venda</b> e <b>Emitir Nota Fiscal</b>.
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 p-3 text-amber-900 text-sm space-y-2">
                      <div className="font-bold flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Escopos faltando ({scopesModal.data.missing.length})
                      </div>
                      <ul className="list-disc ml-5 font-mono text-xs">
                        {scopesModal.data.missing.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                      <div className="text-xs pt-2 border-t border-amber-200">
                        <b>Como resolver:</b> Desconecte e reconecte o Bling.
                        Se após reconectar os escopos ainda faltarem, é preciso
                        marcá-los no cadastro do App no{" "}
                        <a
                          href="https://developer.bling.com.br/aplicativos"
                          target="_blank"
                          rel="noreferrer"
                          className="underline font-bold"
                        >portal do desenvolvedor Bling</a>.
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                      Concedidos pelo Bling ({scopesModal.data.granted.length})
                    </div>
                    {scopesModal.data.granted.length === 0 ? (
                      <div className="text-xs text-slate-500 italic">
                        Nenhum registrado ainda. Reconecte para popular esta lista.
                      </div>
                    ) : (
                      <ul className="font-mono text-xs bg-slate-50 border border-slate-200 p-2 space-y-0.5 max-h-40 overflow-y-auto">
                        {scopesModal.data.granted.map((s) => (
                          <li key={s} className="flex items-center gap-1.5">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            <span className={/^\d+$/.test(s) ? "text-slate-500" : ""}>{s}</span>
                            {/^\d+$/.test(s) && (
                              <span className="text-[10px] text-slate-400 ml-1">
                                (id Bling)
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                      Esperados pela Vyntric ({scopesModal.data.expected.length})
                    </div>
                    <ul className="font-mono text-xs bg-slate-50 border border-slate-200 p-2 space-y-0.5">
                      {scopesModal.data.expected.map((s) => {
                        const has = (scopesModal.data.granted_names || []).includes(s);
                        return (
                          <li key={s} className="flex items-center gap-1.5">
                            {has ? (
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <AlertCircle className="w-3 h-3 text-amber-600" />
                            )}
                            <span className={has ? "" : "text-amber-800 font-bold"}>{s}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CancelBillingDialog({ integration, sub, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const monthlySaved = sub?.extra_bling_brl ?? 49.9;
  const currentLimit = sub?.bling_limit ?? 1;
  const newLimit = Math.max(1, currentLimit - 1);
  const monthlyAfter = (sub?.base_price_brl ?? 149.9) + Math.max(0, newLimit - 1) * monthlySaved;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/${integration.id}/cancel-billing`, {
        reason: (reason || "").trim() || null,
      });
      if (data?.billing_synced === false && data?.stripe_error) {
        toast.warning(
          `Bling cancelado localmente, mas Stripe reportou: ${data.stripe_error}. ` +
          "Revise a assinatura no painel Stripe.",
          { duration: 10000 },
        );
      } else {
        toast.success(
          `Cobrança do Bling "${integration.nickname || integration.name}" cancelada. ` +
          `Próxima fatura: ${brl(monthlyAfter)}/mês.`,
          { duration: 8000 },
        );
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao cancelar cobrança");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="cancel-billing-dialog"
    >
      <div
        className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl border-2 border-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 bg-red-700 text-white">
          <div className="flex items-center gap-2">
            <Ban className="w-5 h-5" />
            <h2 className="font-black uppercase tracking-wider text-sm">
              Cancelar cobrança deste Bling
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-xl leading-none"
            data-testid="cancel-billing-close"
          >×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 p-3 text-xs space-y-1">
            <div><span className="text-slate-500">Integração:</span> <b className="font-mono">{integration.nickname || integration.name}</b></div>
            <div><span className="text-slate-500">ID:</span> <span className="font-mono text-slate-700">{integration.id}</span></div>
            <div><span className="text-slate-500">Status atual:</span> <b>{STATUS_META[integration.status]?.label || integration.status}</b></div>
          </div>
          <div className="border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900 space-y-2">
            <div className="font-bold flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4" />
              O que vai acontecer
            </div>
            <ul className="list-disc ml-5 space-y-1 text-xs">
              <li>A cobrança recorrente de <b>{brl(monthlySaved)}/mês</b> desta conta Bling é removida do Stripe agora — sem esperar o fim do ciclo.</li>
              <li>A próxima fatura será <b>{brl(monthlyAfter)}/mês</b> ({newLimit} Bling{newLimit > 1 ? "s" : ""}).</li>
              <li>Se você já pagou este ciclo, o Stripe aplica <b>crédito proporcional</b> na próxima fatura automaticamente.</li>
              <li>Os dados deste Bling (produtos, pedidos, regras, mapeamentos) são <b>preservados</b>. Você pode conectar outro Bling neste slot depois — ou reativar contratando um novo slot.</li>
              <li>O sync automático e o faturamento de NF-e desta conta Bling <b>param imediatamente</b>.</li>
            </ul>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1 text-slate-600">
              Motivo (opcional — para nossa equipe)
            </span>
            <textarea
              data-testid="cancel-billing-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={400}
              placeholder="Ex.: Comprei sem querer / Vou usar só um Bling / Trocando de plano"
              className="w-full border border-slate-300 px-2 py-2 text-sm focus:outline-none focus:border-red-600"
            />
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="cancel-billing-confirm-check"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs text-slate-700">
              Confirmo que quero <b>cancelar a cobrança recorrente</b> deste Bling.
              Entendo que preciso conectar um novo Bling se quiser esse slot de volta.
            </span>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="cancel-billing-abort"
            className="px-4 py-2 text-xs font-bold uppercase border border-slate-300 bg-white hover:bg-slate-50"
          >
            Voltar
          </button>
          <button
            onClick={submit}
            disabled={busy || !confirmed}
            data-testid="cancel-billing-submit"
            className="px-4 py-2 text-xs font-bold uppercase bg-red-700 hover:bg-red-800 text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
            Cancelar cobrança
          </button>
        </div>
      </div>
    </div>
  );
}
